import { useQuery } from '@tanstack/react-query';
import { api } from '../../../shared/api.js';

function safeGet(path, params) {
  return api
    .get(path, { params })
    .then((r) => r.data)
    .catch((err) => {
      // نطبع الخطأ في console بدلاً من ابتلاعه — يساعد في التشخيص.
      const status = err?.response?.status || '?';
      const msg = err?.response?.data?.message || err?.message || 'unknown';
      console.warn(`[reports] ${path} failed:`, status, msg);
      return null;
    });
}

function todayISO() {
  return new Date().toISOString().slice(0, 10);
}

export function useOverview({ from, to }) {
  return useQuery({
    queryKey: ['reports', 'overview', from, to],
    queryFn: () => safeGet('/reports/overview', { from, to }),
    staleTime: 30_000
  });
}

// الإيراد بالساعة — بالمدى لا بيوم واحد.
// قراءة يوم واحد (`date: range.to`) كانت تُفرغ الرسم كلما اختار المستخدم
// «الأسبوع»/«الشهر» ولم يكن في يومه الأخير بيع. الخادم يجمع أيام المدى
// على نفس شريحة الساعة ويعيد { buckets, totals, days }.
export function useRevenueByHour({ from, to, date } = {}) {
  const hasRange = Boolean(from || to);
  const params = hasRange ? { from, to } : { date: date || todayISO() };
  return useQuery({
    queryKey: ['reports', 'by-hour', params.from || '', params.to || '', params.date || ''],
    queryFn: () => safeGet('/reports/revenue/by-hour', params),
    staleTime: 30_000
  });
}

export function useRevenueByCategory({ from, to }) {
  return useQuery({
    queryKey: ['reports', 'by-category', from, to],
    queryFn: () => safeGet('/reports/revenue/by-category', { from, to }),
    staleTime: 30_000
  });
}

export function useRevenueByCashier({ from, to }) {
  return useQuery({
    queryKey: ['reports', 'by-cashier', from, to],
    queryFn: () => safeGet('/reports/revenue/by-cashier', { from, to }),
    staleTime: 30_000
  });
}

export function useTraffic({ date }) {
  const d = date || todayISO();
  return useQuery({
    queryKey: ['reports', 'traffic', d],
    queryFn: () => safeGet('/reports/traffic', { date: d }),
    staleTime: 30_000
  });
}

export function useTopProducts({ from, to, limit = 10 }) {
  return useQuery({
    queryKey: ['reports', 'top-products', from, to, limit],
    queryFn: () => safeGet('/reports/top-products', { from, to, limit }),
    staleTime: 60_000
  });
}

export function useLowStock() {
  return useQuery({
    queryKey: ['reports', 'low-stock'],
    queryFn: () => safeGet('/reports/low-stock'),
    staleTime: 60_000
  });
}

export function useOverstay() {
  return useQuery({
    queryKey: ['reports', 'sessions-overstay'],
    queryFn: () => safeGet('/reports/sessions/overstay'),
    staleTime: 30_000
  });
}

export function useInventoryValue() {
  return useQuery({
    queryKey: ['reports', 'inventory-value'],
    queryFn: () => safeGet('/reports/inventory/value'),
    staleTime: 60_000
  });
}

export function useFinancialSummary({ from, to }) {
  return useQuery({
    queryKey: ['reports', 'financial-summary', from, to],
    queryFn: () => safeGet('/reports/financial/summary', { from, to }),
    staleTime: 30_000
  });
}

export function useDailyReport({ date }) {
  return useQuery({
    queryKey: ['reports', 'daily', date],
    queryFn: () => safeGet('/reports/daily', { date }),
    enabled: !!date,
    staleTime: 60_000
  });
}

export function useComprehensive({ from, to }) {
  return useQuery({
    queryKey: ['reports', 'comprehensive', from, to],
    queryFn: () => safeGet('/reports/comprehensive', { from, to }),
    staleTime: 30_000
  });
}

// الفواتير الملغاة من مسار التقارير لا من /pos/sales: المسار الثاني يعيد
// مستندات خامة (grandTotal + cashierId مُعبّأ) فكان الجدول يعرض المبلغ صفراً
// واسم الكاشير «—». مسار التقارير يعيد نفس الحقول التي يعرضها الجدول.
export function useCancelledSales({ from, to }) {
  return useQuery({
    queryKey: ['reports', 'cancelled-sales', from, to],
    queryFn: () => safeGet('/reports/sales/cancelled', { from, to, limit: 100 }),
    staleTime: 60_000
  });
}

// تقرير الخصومات — المظروف `{ period, rows, totals, facets }`.
// مفتاح القائمة `rows` لا `items` عن قصد: مُعترض shared/api يفكّ أي مظروف
// فيه `items` إلى مصفوفة مجرّدة، فكانت `totals` و`facets` تضيع معه.
export function useDiscounts({ from, to, userId, type, shiftId }) {
  return useQuery({
    queryKey: ['reports', 'discounts', from, to, userId || '', type || '', shiftId || ''],
    queryFn: () =>
      safeGet('/reports/discounts', {
        from,
        to,
        ...(userId ? { userId } : {}),
        ...(type ? { type } : {}),
        ...(shiftId ? { shiftId } : {})
      }),
    staleTime: 30_000
  });
}

// الفواتير المعلّقة الآن — المظروف `{ rows, count, totalDue, totalBilled }`.
// بلا from/to عن قصد: «معلّقة» حالة قائمة لا حدث داخل فترة، فربطها بمدى
// اليوم كان يُخفي فواتير أمس المفتوحة من لوحة المعلومات.
export function usePendingInvoices({ limit = 100 } = {}) {
  return useQuery({
    queryKey: ['reports', 'pending-invoices', limit],
    queryFn: () => safeGet('/reports/pending-invoices', { limit }),
    staleTime: 15_000
  });
}

// ── المشتريات ────────────────────────────────────────────────────────
// المظروف `{ period, rows, totals, facets }` — المفتاح `rows` لا `items`
// وإلا فكّه مُعترض api فضاعت الإجماليات وقوائم الترشيح.
export function usePurchasesTopProducts({
  from, to, supplierId, warehouseId, sort = 'value', limit = 20
}) {
  return useQuery({
    queryKey: ['reports', 'purchases-top', from, to, supplierId || '', warehouseId || '', sort, limit],
    queryFn: () => safeGet('/reports/purchases/top-products', {
      from,
      to,
      sort,
      limit,
      ...(supplierId ? { supplierId } : {}),
      ...(warehouseId ? { warehouseId } : {})
    }),
    staleTime: 60_000
  });
}

export function useCancelledPurchases({
  from, to, supplierId, warehouseId, limit = 200
}) {
  return useQuery({
    queryKey: ['reports', 'purchases-cancelled', from, to, supplierId || '', warehouseId || '', limit],
    queryFn: () => safeGet('/reports/purchases/cancelled', {
      from,
      to,
      limit,
      ...(supplierId ? { supplierId } : {}),
      ...(warehouseId ? { warehouseId } : {})
    }),
    staleTime: 60_000
  });
}

export function useTopSpenders({ from, to, limit = 5 }) {
  return useQuery({
    queryKey: ['reports', 'top-spenders', from, to, limit],
    queryFn: () => safeGet('/reports/top-spenders', { from, to, limit }),
    staleTime: 60_000
  });
}
